import { FunctionComponent, useEffect, useState } from 'react';
import BreadCumb from '../../components/common/Breadcrumb';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { PROJECT_GOALS } from '../../api/query';
import dangerCricleicon from "../../assets/task/danger-circle_svgrepo.com.svg";
import archiveSvgrepo from "../../assets/task/archive_svgrepo.com.svg";
import indicatorIconimg from "../../assets/task/new-indicator-filled_svgrepo.com.svg";
import heartBeatiocn from "../../assets/task/heart-beat_svgrepo.com.svg";
import testintickIcon from "../../assets/task/tick-box-delivery-check-approved-confirm-accept_svgrepo.com.svg";
import backArrow from "../../assets/task/back_arrow.svg";
import Loader from '../../components/common/Loader';
import { useAuth } from '../../context/AuthContext';
import NotStartedGoals from '../../components/goals/NotStartedGoals';
import InProgressGoals from '../../components/goals/InProgressGoals';
import TestingGoals from '../../components/goals/TestingGoals';
import ClosedGoals from '../../components/goals/ClosedGoals';
import DelayedGoals from '../../components/goals/DelayedGoals';
import { showError } from '../../components/common/ToastNotification';
import InputsNew from '../../components/common/InputsNew';
import DateSelector from '../../components/common/DateSelector';

const Goals: FunctionComponent = () => {
    const navigate = useNavigate()
    const location = useLocation();
    const { userInfo, logout } = useAuth()
    const state = location.state as {
        projectID: any,
        projectName: string,
        projectImage?: string,
        projectMembers?: any
    };

    const [goalFilter, setGoalFilter] = useState<string>('My Goals');
    const filterForGoals = localStorage.getItem("filterForGoals");
    const [searchQuery, setSearchQuery] = useState("");
    const [startDate, setStartDate] = useState<string | null>(
        localStorage.getItem("kanbanGoalStartDate") || null
    );
    const [endDate, setEndDate] = useState<string | null>(
        localStorage.getItem("kanbanGoalEndDate") || null
    );

    const employeeID = userInfo?.employee_id

    const { projectID, projectName, projectImage, projectMembers } = state

    //api
    const [
        getProjectGoals,
        { data: projectGoals, loading: projectGoalsLoading, error },
    ] = useLazyQuery(PROJECT_GOALS, {
        fetchPolicy: 'no-cache'
    });

    if (error) {
        if ((error.message.includes("Session has expired. Please login again.")) || (error.message.includes("Unauthorized access, please login again."))) {
            showError(error.message)
            logout();
            navigate('/')
        }
    }

    useEffect(() => {
        if (filterForGoals) {
            setGoalFilter(filterForGoals);
            if (filterForGoals == "All Goals") {
                handleAllGoalsClick()
            } else if (filterForGoals == "My Goals") {
                handleMyGoalsClick()
            } else if (filterForGoals == "Customer Success") {
                handleCSClick()
            } else if (filterForGoals == "Product Development") {
                handlePDClick()
            } else if (filterForGoals == "Marketing") {
                handleMClick()
            }
        } else {
            if (projectID && employeeID) {
                getProjectGoals({
                    variables: {
                        project_id: parseInt(projectID),
                        is_archived: false,
                        employee_id: parseInt(employeeID)
                    },
                });
            }
        }
    }, [projectID, employeeID])

    const filterOutDelayed = (goals: any[]) =>
        goals?.filter(goal =>
            !goal?.goal_tags_values?.some((tag: any) => tag.tags_name === "Delayed")
        );

    // Filter goals by search query (searches in goal title and member names)
    const filterBySearch = (goals: any[]) => {
        if (!searchQuery) return goals;
        const query = searchQuery.toLowerCase();
        return goals?.filter((goal: any) => {
            // Check if goal title matches
            const titleMatches = goal?.title?.toLowerCase().includes(query);

            // Check if any member name matches
            const memberMatches = goal?.goal_members?.some((member: any) =>
                member?.member_name?.toLowerCase().includes(query)
            );

            return titleMatches || memberMatches;
        });
    };

    const notStarted = projectGoals?.project_goals?.grouped_goals?.filter((nsGoals: any) =>
        nsGoals?.status_name === "Not Started"
    )?.map((group: any) => ({
        ...group,
        goals: filterBySearch(filterOutDelayed(group.goals))
    }))

    const inProgress = projectGoals?.project_goals?.grouped_goals?.filter((ipGoals: any) =>
        ipGoals?.status_name === "In Progress"
    )?.map((group: any) => ({
        ...group,
        goals: filterBySearch(filterOutDelayed(group.goals))
    }));

    const testing = projectGoals?.project_goals?.grouped_goals?.filter((tGoals: any) =>
        tGoals?.status_name === "Testing"
    )?.map((group: any) => ({
        ...group,
        goals: filterBySearch(filterOutDelayed(group.goals))
    }));

    const closed = projectGoals?.project_goals?.grouped_goals?.filter((cGoals: any) =>
        cGoals?.status_name === "Closed"
    )?.map((group: any) => ({
        ...group,
        goals: filterBySearch(group.goals)
    }))

    const delayed = filterBySearch(
        projectGoals?.project_goals?.grouped_goals
            ?.filter((group: any) => group.status_name !== "Closed") // exclude Closed
            .flatMap((group: any) => group.goals || []) // flatten all goals from the remaining statuses
            .filter((goal: any) =>
                goal?.goal_tags_values?.some((tag: any) => tag.tags_name === "Delayed")
            )
    );

    function handleAllGoalsClick() {
        setGoalFilter('All Goals')
        localStorage.setItem("filterForGoals", 'All Goals');
        getProjectGoals({
            variables: {
                project_id: parseInt(projectID),
                is_archived: false,
                employee_id: null,
                start_date: startDate,
                end_date: endDate
            },
        });
    }

    function handleMyGoalsClick() {
        setGoalFilter('My Goals')
        localStorage.setItem("filterForGoals", 'My Goals');
        getProjectGoals({
            variables: {
                project_id: parseInt(projectID),
                is_archived: false,
                employee_id: parseInt(employeeID),
                start_date: startDate,
                end_date: endDate
            },
        });
    }

    function handleCSClick() {
        setGoalFilter('Customer Success')
        localStorage.setItem("filterForGoals", 'Customer Success');
        getProjectGoals({
            variables: {
                project_id: parseInt(projectID),
                is_archived: false,
                employee_id: null,
                goal_for: "Customer Success",
                start_date: startDate,
                end_date: endDate
            },
        });
    }

    function handlePDClick() {
        setGoalFilter('Product Development')
        localStorage.setItem("filterForGoals", 'Product Development');
        getProjectGoals({
            variables: {
                project_id: parseInt(projectID),
                is_archived: false,
                employee_id: null,
                goal_for: "Product Development",
                start_date: startDate,
                end_date: endDate
            },
        });
    }

    function handleMClick() {
        setGoalFilter('Marketing')
        localStorage.setItem("filterForGoals", 'Marketing');
        getProjectGoals({
            variables: {
                project_id: parseInt(projectID),
                is_archived: false,
                employee_id: null,
                goal_for: "Marketing",
                start_date: startDate,
                end_date: endDate
            },
        });
    }

    function handleSearchClick() {
        if (!startDate || !endDate) {
            showError("Please select both start and end dates");
            return;
        }

        // Save dates to localStorage
        localStorage.setItem("kanbanGoalStartDate", startDate);
        localStorage.setItem("kanbanGoalEndDate", endDate);

        // Re-trigger the current filter with date range
        if (goalFilter === 'All Goals') {
            handleAllGoalsClick();
        } else if (goalFilter === 'My Goals') {
            handleMyGoalsClick();
        } else if (goalFilter === 'Customer Success') {
            handleCSClick();
        } else if (goalFilter === 'Product Development') {
            handlePDClick();
        } else if (goalFilter === 'Marketing') {
            handleMClick();
        }
    }

    function handleClearClick() {
        setStartDate(null);
        setEndDate(null);
        setSearchQuery("");

        // Clear dates from localStorage
        localStorage.removeItem("kanbanGoalStartDate");
        localStorage.removeItem("kanbanGoalEndDate");

        // Re-trigger the current filter without date range
        if (goalFilter === 'All Goals') {
            getProjectGoals({
                variables: {
                    project_id: parseInt(projectID),
                    is_archived: false,
                    employee_id: null
                },
            });
        } else if (goalFilter === 'My Goals') {
            getProjectGoals({
                variables: {
                    project_id: parseInt(projectID),
                    is_archived: false,
                    employee_id: parseInt(employeeID)
                },
            });
        } else if (goalFilter === 'Customer Success') {
            getProjectGoals({
                variables: {
                    project_id: parseInt(projectID),
                    is_archived: false,
                    employee_id: null,
                    goal_for: "Customer Success"
                },
            });
        } else if (goalFilter === 'Product Development') {
            getProjectGoals({
                variables: {
                    project_id: parseInt(projectID),
                    is_archived: false,
                    employee_id: null,
                    goal_for: "Product Development"
                },
            });
        } else if (goalFilter === 'Marketing') {
            getProjectGoals({
                variables: {
                    project_id: parseInt(projectID),
                    is_archived: false,
                    employee_id: null,
                    goal_for: "Marketing"
                },
            });
        }
    }

    function handleGoalClick(goalID: string) {
        navigate('/goal_view', {
            state: {
                goalID: goalID,
                projectName: projectName
            }
        })
    }

    //     function handleGoalClick(goalID: string, goalName: string, goalType: string, startGoalDate: string, endGoalDate: string, goalStaticID: string, goalDescription: string) {
    //     // navigate('/goal_view', {
    //     //     state: {
    //     //         goalID: goalID,
    //     //         projectName: projectName,
    //     //         projectMembers: projectMembers,
    //     //     }
    //     // })

    //     navigate('/edit_goal', {
    //         state: {
    //             projectName: projectName,
    //             projectID: projectID,
    //             goalName: goalName,
    //             goalID: goalID,
    //             goalType: goalType,
    //             startGoalDate: startGoalDate,
    //             endGoalDate: endGoalDate,
    //             goalStaticID: goalStaticID,
    //             goalDescription: goalDescription
    //         }
    //     })
    // }

    return (
        <>
            <div className="w-full relative min-h-screen overflow-hidden flex flex-row items-start justify-center flex-wrap content-start py-40 px-20  mq1490:px-[30px]  mq575:px-5  box-border text-left text-base font-manrope">
                <div className="min-w-full flex flex-col items-start justify-start gap-[24px] ">
                    <BreadCumb clientName={projectName} />
                    {/* <div className="self-stretch rounded flex flex-col items-start justify-start text-dimgray">
                    <div className="flex flex-row items-center justify-start">
                        <div className="overflow-hidden flex flex-row items-center justify-start gap-2">
                            <div className="relative [text-decoration:underline]">My Projects</div>
                            <img className="w-4 relative h-4 overflow-hidden shrink-0" alt="" src="Right 16px.svg" />
                            <div className="relative font-semibold text-gray-200">Wizkind</div>
                        </div>
                    </div>
                </div> */}
                    <div className="self-stretch flex flex-row items-center justify-start text-2xl gap-[5px ]">
                        <img className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img" alt="" src={backArrow}
                            onClick={() => navigate(-1)}
                        />
                        <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">{projectName}</h3>
                    </div>
                    <div className="self-stretch flex  flex-row items-start justify-between gap-6 mq1275:flex-col ">


                        {/* Right: Filters Section */}
                        <div className=" dark:bg-slate-800 grid gap-[24px] p-8 w-full dark-bg-white-items rounded-lg shadow-sm border border-gray-100 dark:border-slate-700">
                            {/* Left: Project Logo + From Template button */}
                            {/* <div className="flex items-center justify-between">
                                <img className="w-[150px] mq575:h-8 object-contain relative lightmode_logo" loading="lazy" alt="" src={projectImage} />
                                <button
                                    onClick={() => navigate('/create_goal_from_template', {
                                        state: { projectID, projectName, projectImage, projectMembers }
                                    })}
                                    className="cursor-pointer h-[43px] text-white [border:none] py-[12.5px] px-[24px] bg-surface-primary-surface rounded-radiuscomponent-button flex flex-row items-center justify-center gap-[8px] hover:bg-dodgerblue"
                                >
                                    <span className="text-sm font-semibold">+ From Template</span>
                                </button>
                            </div> */}
                            <div className="flex flex-col gap-5 w-full">
                                {/* Date Range Filter Section */}
                                <div className="flex flex-col gap-3 w-full">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-bold text-gray-700 dark:text-gray-200  tracking-wide">Date Range Filter</span>
                                    </div>
                                    <div className="goal-filter-section-blg items-end grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[8px] min-w-full">
                                        <div className="">
                                            <DateSelector
                                                label="Start Date"
                                                selectedDate={startDate}
                                                onChange={(date) => {
                                                    setStartDate(date);
                                                    setEndDate(null);
                                                }}
                                                nothideParsedValue={true}
                                            />
                                        </div>
                                        <div className="">
                                            <DateSelector
                                                label="End Date"
                                                selectedDate={endDate}
                                                onChange={(date) => setEndDate(date)}
                                                minDate={startDate ? new Date(startDate) : undefined}
                                                disabled={!startDate}
                                                nothideParsedValue={true}
                                            />
                                        </div>
                                        <div className="flex gap-2 mq950:w-full">
                                            <button
                                                onClick={handleSearchClick}
                                                className="cursor-pointer h-[43px] text-white [border:none] py-[12.5px] px-[51px] bg-surface-primary-surface rounded-radiuscomponent-button flex flex-row items-center justify-center gap-[12px] hover:bg-dodgerblue"
                                            >
                                             <span className="relative text-sm font-body-xs-semi-bold text-[#2b2b2b] text-center inline-block">   Apply Filter</span>
                                            </button>
                                            <button
                                                onClick={handleClearClick}
                                                className="cursor-pointer h-[43px] py-2.5 px-[42px] bg-surface-bg-primary rounded-radiuscomponent-button btn-text-purple box-border flex flex-row items-center justify-center gap-[12px] min-w-[88px] border-[2px] border-solid border-surface-primary-surface hover:bg-gainsboro-100 hover:box-border hover:border-[2px] hover:border-solid hover:border-dodgerblue"
                                            >
                                                <span className="relative text-sm font-body-xs-semi-bold text-[#2b2b2b] text-center inline-block">Clear</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Divider */}
                                {/* <div className="border-t border-gray-200 dark:border-slate-600"></div> */}

                                {/* Search by Name Section */}
                                <div className="flex flex-col gap-[8px]">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-bold text-gray-700 dark:text-gray-200  tracking-wide">Search by Name</span>
                                    </div>
                                    <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[8px] min-w-full">
                                        <InputsNew
                                            label=""
                                            type="text"
                                            contentPlaceholder="Search by goal name or member name..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="m-0 self-stretch rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg max-w-full mq825:pt-5 mq825:pb-5 mq825:box-border mq1425:pt-[21px] mq1425:pb-[21px] mq1425:box-border">

                        <div className="self-stretch flex flex-row items-center justify-start text-right text-darkmagenta filterSvgrepo-dv">
                            <div className="flex-1 flex flex-row items-center justify-end gap-[8px]">
                                {/* <img className="w-6 relative max-h-full overflow-hidden shrink-0" alt="" src={filterSvgrepo} /> */}
                                <div className={`relative link-filterSvgrepo-dv cursor-pointer inactive_item_blgs ${goalFilter === 'All Goals' ? '[text-decoration:underline] font-semibold active_item_blgs' : ''}`}
                                    onClick={handleAllGoalsClick}
                                >
                                    All Goals
                                </div>
                                {/* <div className="relative link-filterSvgrepo-dv">Development</div>
                                <div className="relative link-filterSvgrepo-dv">Experience</div> */}
                                <div className={`relative link-filterSvgrepo-dv cursor-pointer inactive_item_blgs  ${goalFilter === 'My Goals' ? '[text-decoration:underline] font-semibold active_item_blgs' : ''}`}
                                    onClick={handleMyGoalsClick}
                                >
                                    My Goals
                                </div>
                                <div className={`relative link-filterSvgrepo-dv cursor-pointer inactive_item_blgs ${goalFilter === 'Marketing' ? '[text-decoration:underline] font-semibold active_item_blgs' : ''}`}
                                    onClick={handleMClick}
                                >
                                    Marketing
                                </div>
                                <div className={`relative link-filterSvgrepo-dv cursor-pointer inactive_item_blgs ${goalFilter === 'Customer Success' ? '[text-decoration:underline] font-semibold active_item_blgs' : ''}`}
                                    onClick={handleCSClick}
                                >
                                    Customer Success
                                </div>
                                <div className={`relative link-filterSvgrepo-dv cursor-pointer inactive_item_blgs ${goalFilter === 'Product Development' ? '[text-decoration:underline] font-semibold active_item_blgs' : ''}`}
                                    onClick={handlePDClick}
                                >
                                    Product Development
                                </div>
                            </div>
                        </div>
                        <div className=" goal_main_section_dv  viewdoclist_tabel_blg min-w-full overflow-hidden assessorform_main_tbl client-hist-main">
                            <div className=" inner_goal_main_section_dv overflow-x-auto client-report-dtnamic-tbl">
                                <div className="goal_main_section_row grid grid-cols-5 mq1400:grid-5 mq1275:grid-cols-5 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px]  min-w-full self-stretch flex flex-row items-start justify-start gap-6 mq991:gap-[8px] text-lg">
                                    {/* not started */}
                                    <div className="flex-1 shadow-[0px_5px_5px_rgba(0,_0,_0,_0.1)] rounded bg-aliceblue flex flex-col items-start justify-center">
                                        <div className="self-stretch rounded-t rounded-b-none bg-white border-whitesmoke border-solid border-[1px] box-border flex flex-row items-center justify-between p-2 gap-0">
                                            <div className="flex flex-row items-center justify-start gap-2">
                                                <img className="w-[30px] relative max-h-full overflow-hidden shrink-0" alt="" src={indicatorIconimg} />
                                                <div className="rounded-[50px] bg-aliceblue border-whitesmoke border-solid border-[1px] box-border flex flex-row items-center justify-center py-1 px-4">
                                                    <b className="relative">Not Started</b>
                                                </div>
                                                <div className="w-[30px] rounded-[50px] bg-aliceblue h-[30px] flex flex-col items-center justify-center p-0.5 box-border text-base">
                                                    <div className="relative font-semibold">{notStarted && (notStarted[0] ? notStarted[0].goals?.length : 0)}</div>
                                                </div>
                                            </div>
                                            {/* <div className="cursor-pointer flex flex-col items-start justify-start p-[10px] plus-task-btn taskdash-plush-img rounded">
                                        <img className="w-4 h-4 relative overflow-hidden shrink-0" loading="lazy" alt="" src={ioniconaaddoutline} />
                                    </div> */}
                                        </div>
                                        <div className="self-stretch flex flex-col items-start justify-start p-4 gap-4 text-base">
                                            {notStarted && notStarted[0]?.goals?.length > 0 ? (
                                                notStarted[0]?.goals?.map((ns: any) => {
                                                    return (
                                                        <NotStartedGoals handleGoalClick={handleGoalClick} ns={ns} key={ns.id} projectMembers={projectMembers} />
                                                    )
                                                })
                                            ) : <>No new goal</>}
                                        </div>
                                    </div>

                                    {/* In Progress */}
                                    <div className="flex-1 shadow-[0px_1px_5px_rgba(0,_0,_0,_0.1)] rounded bg-antiquewhite flex flex-col items-start justify-center">
                                        <div className="self-stretch rounded-t rounded-b-none bg-white border-whitesmoke border-solid border-[1px] box-border flex flex-row items-center justify-between p-2 gap-0">
                                            <div className="flex flex-row items-center justify-start gap-2">
                                                <img className="w-[30px] relative max-h-full overflow-hidden shrink-0" alt="" src={heartBeatiocn} />
                                                <div className="rounded-[50px] bg-antiquewhite border-whitesmoke border-solid border-[1px] box-border flex flex-row items-center justify-center py-1 px-4">
                                                    <b className="relative">In Progress</b>
                                                </div>
                                                <div className="w-[30px] rounded-[50px] bg-antiquewhite h-[30px] flex flex-col items-center justify-center p-0.5 box-border text-base">
                                                    <div className="relative font-semibold">{inProgress && (inProgress[0] ? inProgress[0].goals?.length : 0)}</div>
                                                </div>
                                            </div>
                                            {/* <div className="w-6 rounded h-6 flex flex-row items-center justify-center p-2.5 box-border">
                                        <img className="w-3 relative h-3" alt="" src="plus-lg.svg" />
                                    </div> */}
                                        </div>
                                        {inProgress && inProgress[0]?.goals?.length > 0 ? (
                                            inProgress[0]?.goals?.map((g: any) => {
                                                return (
                                                    <InProgressGoals handleGoalClick={handleGoalClick} g={g} key={g.id} projectMembers={projectMembers} />
                                                )
                                            })
                                        ) : <span className="self-stretch flex flex-col items-start justify-start p-4 gap-4 text-base h-[100%]">No goal is in progress</span>}
                                    </div>

                                    {/* Testing */}
                                    <div className="flex-1 shadow-[0px_1px_5px_rgba(0,_0,_0,_0.1)] rounded bg-antiquewhite-testing flex flex-col items-start justify-center">
                                        <div className="self-stretch rounded-t rounded-b-none testing-project-section-dv bg-white border-whitesmoke border-solid border-[1px] box-border flex flex-row items-center justify-between p-2 gap-0">
                                            <div className="flex flex-row items-center justify-start gap-2">
                                                <img className="w-[30px] relative max-h-full overflow-hidden shrink-0" alt="" src={testintickIcon} />
                                                <img className="w-[30px] relative max-h-full overflow-hidden shrink-0 hidden" alt="" src="review_svgrepo.com.svg" />
                                                <div className="rounded-[50px] bg-antiquewhite-testing border-whitesmoke border-solid border-[1px] box-border flex flex-row items-center justify-center py-1 px-4">
                                                    <b className="relative ">Testing</b>
                                                </div>
                                                <div className="w-[30px] rounded-[50px] bg-antiquewhite-testing h-[30px] flex flex-col items-center justify-center p-0.5 box-border text-base">
                                                    <div className="relative font-semibold">{testing && (testing[0] ? testing[0]?.goals?.length : 0)}</div>
                                                </div>
                                            </div>
                                            <div className="w-6 rounded h-6 flex flex-row items-center justify-center p-2.5 box-border">
                                                <img className="w-3 relative h-3" alt="" src="plus-lg.svg" />
                                            </div>
                                        </div>
                                        <div className="self-stretch flex flex-col items-start justify-start p-4 gap-4 text-base">
                                            {testing && testing[0]?.goals?.length > 0 ? (
                                                testing[0]?.goals?.map((g: any) => {
                                                    return (
                                                        <TestingGoals g={g} handleGoalClick={handleGoalClick} key={g.id} projectMembers={projectMembers} />
                                                    )
                                                })
                                            ) : <>No goal is in tesing</>}
                                        </div>
                                    </div>

                                    {/* closed */}
                                    <div className="flex-1 shadow-[0px_1px_5px_rgba(0,_0,_0,_0.1)] rounded bg-honeydew flex flex-col items-start justify-center">
                                        <div className="self-stretch rounded-t rounded-b-none bg-white border-whitesmoke border-solid border-[1px] box-border flex flex-row items-center justify-between p-2 gap-0">
                                            <div className="flex flex-row items-center justify-start gap-2">
                                                <img className="w-[30px] relative max-h-full overflow-hidden shrink-0" alt="" src={archiveSvgrepo} />
                                                <div className="rounded-[50px] bg-honeydew border-whitesmoke border-solid border-[1px] box-border flex flex-row items-center justify-center py-1 px-4">
                                                    <b className="relative">Closed</b>
                                                </div>
                                                <div className="w-[30px] rounded-[50px] bg-honeydew h-[30px] flex flex-col items-center justify-center p-0.5 box-border text-base">
                                                    <div className="relative font-semibold">{closed && (closed[0] ? closed[0]?.goals?.length : 0)}</div>
                                                </div>
                                            </div>
                                            <div className="w-6 rounded h-6 flex flex-row items-center justify-center p-2.5 box-border">
                                                <img className="w-3 relative h-3" alt="" src="plus-lg.svg" />
                                            </div>
                                        </div>
                                        <div className="self-stretch flex flex-col items-start justify-start p-4 gap-4 text-base h-[100%]">
                                            {closed && closed[0]?.goals?.length > 0 ? (
                                                closed && closed[0]?.goals?.map((cg: any) => {
                                                    return (
                                                        <ClosedGoals handleGoalClick={handleGoalClick} cg={cg} key={cg.id} projectMembers={projectMembers} />
                                                    )
                                                })
                                            ) : <>No goal is closed</>}
                                        </div>
                                    </div>

                                    {/* delayed */}
                                    <div className="flex-1 shadow-[0px_1px_5px_rgba(0,_0,_0,_0.1)] rounded bg-crimson-200 flex flex-col items-start justify-center">
                                        <div className="self-stretch rounded-t rounded-b-none bg-white border-whitesmoke border-solid border-[1px] box-border flex flex-row items-center justify-between p-2 gap-0">
                                            <div className="flex flex-row items-center justify-start gap-2">
                                                <img className="w-[30px] relative max-h-full overflow-hidden shrink-0" alt="" src={dangerCricleicon} />
                                                <div className="bg-border-customred-dv rounded-[50px] bg-crimson-200 border-whitesmoke border-solid border-[1px] box-border flex flex-row items-center justify-center py-1 px-4">
                                                    <b className="relative">Delayed</b>
                                                </div>
                                                <div className="w-[30px] bg-border-customred-dv rounded-[50px] bg-crimson-200 h-[30px] flex flex-col items-center justify-center p-0.5 box-border text-base">
                                                    <div className="relative font-semibold">{delayed && delayed?.length}</div>
                                                </div>
                                            </div>
                                            <div className="w-6 rounded h-6 flex flex-row items-center justify-center p-2.5 box-border">
                                                <img className="w-3 relative h-3" alt="" src="plus-lg.svg" />
                                            </div>
                                        </div>
                                        <div className="self-stretch flex flex-col items-start justify-start p-4 text-base bg-border-customred-dv gap-[16px]">
                                            {delayed && delayed?.length > 0 ? (
                                                delayed.map((d: any) => {
                                                    return (
                                                        <DelayedGoals handleGoalClick={handleGoalClick} d={d} key={d.id} projectMembers={projectMembers} />
                                                    )
                                                })
                                            ) : <>Great! No goal is running delayed</>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {projectGoalsLoading && (
                <Loader />
            )}
        </>
    );
};

export default Goals;
